/* eslint-disable react-hooks/exhaustive-deps */
/**
 * B100130 セット商品マスタメンテ
 */
import { Column } from "@material-table/core";
import Big from "big.js";
import {
  B000200SetshohinNm,
  B100130Create,
  B100130CreateMeiRequest,
  B100130CreateRequest,
  B100130DeleteMeiRequest,
  B100130Find,
  B100130FindRequest,
  B100130HandleChangeShohinCd,
  B100130Shohin,
  B100130WhTanka,
  catchRestError,
} from "clients/rest";
import {
  SFrame,
  SLabel,
  SNumberField,
  STextField,
  msgState,
  SNormalButton,
  SFileButton,
  SConfirmDialog,
  SCancelButton,
  SEntryTab,
  SText,
  STable,
} from "components";
import { getClientMessage } from "messages";
import { labels } from "messages/labels";
import { B000200 } from "pages/b000/B000200";
import { createRef, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { digit, mapIds } from "services";
import { focusState, navigateTypeState, useFocus } from "states";

// 呼び出し時のプロパティ
type Props = Partial<{}>;

// コンポーネントで設定されるフィールドの型を定義する
// ヘッダ部
type Fields = Partial<{
  setshohinCd: string;
  shohinNm: string;
  jodaitankaGokei: string;
  dirtyFields: {
    setshohinCd: string;
  };
}>;

// タブ部
type MeiFields = MeiData &
  Partial<{
    tab: boolean;
    dirtyFields: {
      shohinCd: string;
      sotosunW: string;
      sotosunH: string;
    };
  }>;

// 明細データ
type MeiData = Partial<{
  gyoNo: number;
  shohinCd: string;
  shohinNm: string;
  sotosunW: string;
  sotosunH: string;
  uchisunW: string;
  uchisunH: string;
  jodaitanka: string;
  koseisu: string;
  tankaKbn: number;
  whtankaCd: string;
  crtDate: string;
  crtshainCd: string;
  version: string;
}>;

//テーブルデータの定義
type Data = {
  gyoNo: number;
  shohinCd: string;
} & Partial<{
  shohinNm: string;
  sotosunView: string;
  uchisunView: string;
  sotosunW: string;
  sotosunH: string;
  uchisunW: string;
  uchisunH: string;
  jodaitanka: string;
  koseisu: string;
  tankaKbn: number;
  whtankaCd: string;
  crtDate: string;
  crtshainCd: string;
  version: string;
}>;

// 明細削除データ
type DelData = Partial<{
  setshohinCd: string;
  gyoNo: number;
  shohinCd: string;
  version: string;
}>;

// フィールドの初期値を設定する。
// ヘッダ部
const defaultFields: Fields = {
  setshohinCd: "",
  shohinNm: "",
  jodaitankaGokei: "",
  dirtyFields: {
    setshohinCd: "",
  },
};

// タブ部
const defaultMeiFields: MeiFields = {
  tab: true,
  dirtyFields: {
    shohinCd: "",
    sotosunW: "",
    sotosunH: "",
  },
  gyoNo: undefined,
  shohinCd: "",
  shohinNm: "",
  sotosunW: "",
  sotosunH: "",
  uchisunW: "",
  uchisunH: "",
  jodaitanka: "",
  koseisu: "",
  tankaKbn: undefined,
  whtankaCd: "",
  crtDate: "",
  crtshainCd: "",
  version: "",
};

// テーブル
const columns: Column<Data>[] = [
  { field: "gyoNo", title: "行　", width: "3rem", align: "right" },
  { field: "shohinCd", title: "商品コード", width: "8rem" },
  { field: "shohinNm", title: "商品名", width: "25rem" },
  {
    field: "sotosunView",
    title: "WH(外)",
    width: "7rem",
    render: (data) => {
      if (data.sotosunW && Number(data.sotosunW) > 0) {
        return data.sotosunW + " x " + data.sotosunH;
      }
    },
  },
  {
    field: "uchisunView",
    title: "WH(内)",
    width: "7rem",
    render: (data) => {
      if (data.uchisunW && Number(data.uchisunW) > 0) {
        return data.uchisunW + " x " + data.uchisunH;
      }
    },
  },
  { field: "sotosunH", title: "外高さ", hidden: true },
  { field: "sotosunW", title: "外幅", hidden: true },
  { field: "uchisunW", title: "内幅", hidden: true },
  { field: "uchisunH", title: "内高さ", hidden: true },
  {
    field: "jodaitanka",
    title: "上代単価　",
    width: "5rem",
    align: "right",
    render: (data) => digit(data.jodaitanka),
  },
  {
    field: "koseisu",
    title: "構成数　",
    width: "5rem",
    align: "right",
    render: (data) => digit(data.koseisu),
  },
  { field: "tankaKbn", hidden: true },
  { field: "whtankaCd", hidden: true },
  { field: "crtDate", hidden: true },
  { field: "crtshainCd", hidden: true },
  { field: "version", hidden: true },
];

const focusId = "B100130";

// B100130 セット商品マスタメンテ
export const B100130 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};

const Page = (props: Props) => {
  // 状態管理の初期設定
  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });
  const meiMethods = useForm<MeiFields>({
    mode: "onBlur",
    defaultValues: defaultMeiFields,
  });
  // テーブル管理
  const tableRef = createRef();
  const [rows, setRows] = useState<Data[]>([]);
  // テーブル行で選択されたデータ
  const [rowData, setRowData] = useState<Partial<Data>>();

  // 明細削除データ
  const [delData, setDelData] = useState<DelData[]>([]);

  const resetDirtyFields = () => {
    const fields = methods.getValues();
    methods.setValue("dirtyFields.setshohinCd", fields.setshohinCd ?? "");
  };

  const resetMeiDirtyFields = () => {
    const fields = meiMethods.getValues();
    meiMethods.setValue("dirtyFields.shohinCd", fields.shohinCd ?? "");
    meiMethods.setValue("dirtyFields.sotosunW", fields.sotosunW ?? "");
    meiMethods.setValue("dirtyFields.sotosunH", fields.sotosunH ?? "");
  };

  // フォーカス設定
  const { focus, register } = useRecoilValue(focusState(focusId));
  // 遷移元データ取得用
  const navigate = useNavigate();
  // 画面遷移の処理
  const [menuNavigate, setMenuNavigate] = useRecoilState(navigateTypeState);
  // エラー表示
  const setMsg = useSetRecoilState(msgState);

  // 初期表示イベント
  useEffect(() => {
    focus(1);
  }, []);

  // 明細変更
  useEffect(() => {
    //合計金額算出
    handleGokeiCalculating();
  }, [rows]);

  // 検索時の初期化処理
  const handleResetSearch = () => {
    // 選択されているデータを空にする
    setRowData({});
    (tableRef.current as any).handleClearSelect();
  };

  // 検索ボタンクリックイベント
  const handleClickSearchButton = () => {
    handleResetSearch();

    // 明細クリア
    setRows([]);
    // 明細入力部クリア
    meiMethods.reset();
    resetMeiDirtyFields();
    setDelData([]);

    // 検索フォームのデータを取得する。
    const fields = methods.getValues();

    // 検索パラメータを作成する。
    const param: B100130FindRequest = {
      setshohinCd: fields.setshohinCd!,
    };

    // API呼出し
    B100130Find(param)
      .then((value) => {
        // 明細へのマッピング
        const data = value.data.map<Data>((value) => {
          // 行のデータセット
          const data: Data = {
            gyoNo: value.gyoNo,
            shohinCd: value.shohinCd ?? "",
            shohinNm: value.shohinNm ?? "",
            sotosunW: value.sotosunW ?? "",
            sotosunH: value.sotosunH ?? "",
            uchisunW: value.uchisunW ?? "",
            uchisunH: value.uchisunH ?? "",
            jodaitanka: value.jodaitanka ?? "",
            koseisu: value.koseisu ?? "",
            tankaKbn: value.tankaKbn ?? "",
            whtankaCd: value.whtankaCd ?? "",
            crtDate: value.crtDate ?? "",
            crtshainCd: value.crtshainCd ?? "",
            version: value.version ?? "",
          };
          return data;
        });
        // テーブルに反映
        setRows(data);

        // 削除対象のキー情報を設定
        const delData = value.data.map<DelData>((value) => {
          // 行のデータセット
          const data: DelData = {
            setshohinCd: fields.setshohinCd,
            gyoNo: value.gyoNo,
            shohinCd: value.shohinCd ?? "",
            version: value.version ?? "",
          };
          return data;
        });
        setDelData(delData);
      })
      // エラー時
      .catch((reason) => {
        catchRestError(reason, navigate, setMsg);
      });
  };

  // テーブル行ダブルクリックイベント
  const handleDoubleClickTableRow = (rowData: Data) => {
    meiMethods.reset();
    // タブを編集にする
    meiMethods.setValue("tab", false);
    // ダブルクリックした行を選択状態にする
    setRowData(rowData);
    // 明細入力部に値を設定する
    meiMethods.setValue("gyoNo", rowData.gyoNo);
    meiMethods.setValue("shohinCd", rowData.shohinCd);
    meiMethods.setValue("shohinNm", rowData.shohinNm);
    meiMethods.setValue("sotosunW", rowData.sotosunW);
    meiMethods.setValue("sotosunH", rowData.sotosunH);
    meiMethods.setValue("uchisunW", rowData.uchisunW);
    meiMethods.setValue("uchisunH", rowData.uchisunH);
    meiMethods.setValue("jodaitanka", rowData.jodaitanka);
    meiMethods.setValue("koseisu", rowData.koseisu);
    meiMethods.setValue("tankaKbn", rowData.tankaKbn);
    meiMethods.setValue("whtankaCd", rowData.whtankaCd);
    meiMethods.setValue("crtDate", rowData.crtDate);
    meiMethods.setValue("crtshainCd", rowData.crtshainCd);
    meiMethods.setValue("version", rowData.version);
    resetMeiDirtyFields();
  };

  // タブ切り替え
  const handleChangeTab = () => {
    // 行選択解除
    setRowData(undefined);
    // 明細入力部の値を初期化
    meiMethods.reset();
    resetMeiDirtyFields();
  };

  // 更新
  const handleClickUpdateButton = () => {
    // タブ部のデータ取得
    const fields = methods.getValues();

    // DB登録するデータの作成
    // 明細マッピング
    const b100130CreateMeiRequest = rows.map<B100130CreateMeiRequest>(
      (value) => {
        // 行のデータセット
        const data: B100130CreateMeiRequest = {
          gyoNo: value.gyoNo,
          shohinCd: value.shohinCd,
          sotosunW: value.sotosunW,
          sotosunH: value.sotosunH,
          uchisunW: value.uchisunW,
          uchisunH: value.uchisunH,
          jodaitanka: value.jodaitanka,
          koseisu: value.koseisu!,
          crtDate: value.crtDate,
          crtshainCd: value.crtshainCd,
          version: value.version,
        };
        return data;
      }
    );

    const b100130DeleteMeiRequest = delData.map<B100130DeleteMeiRequest>(
      (value) => {
        // 行のデータセット
        const data: B100130DeleteMeiRequest = {
          gyoNo: value.gyoNo,
          shohinCd: value.shohinCd,
          version: value.version,
        };
        return data;
      }
    );
    const data: B100130CreateRequest = {
      setshohinCd: fields.setshohinCd!,
      b100130CreateMeiRequest: b100130CreateMeiRequest,
      b100130DeleteMeiRequest: b100130DeleteMeiRequest,
    };

    // API呼出し
    B100130Create(data)
      .then(() => {
        // ヘッダークリア
        methods.reset();
        resetDirtyFields();

        // 行選択解除
        setRowData(undefined);
        // 明細クリア
        setRows([]);
        // 明細入力部クリア
        meiMethods.reset();
        resetMeiDirtyFields();
        setDelData([]);

        // 登録完了メッセージ表示
        setMsg(getClientMessage("B100130_001"));
      })
      .catch((reason) => {
        catchRestError(reason, navigate, setMsg);
      });
  };

  // 明細登録
  const handleClickCreateMeiButton = () => {
    // フィールドチェック
    meiMethods.handleSubmit(
      () => {
        // 明細に設定するデータを取得
        const gyoNo: number = rows.length + 1;
        const data = handleDataMapping(gyoNo);

        // 明細を設定
        setRows([...rows, data]);

        // 明細入力部クリア
        meiMethods.reset();
        resetMeiDirtyFields();

        focus(3);
      },
      () => {
        focus(3);
      }
    )();
  };

  // 明細更新
  const handleClickUpdateMeiButton = () => {
    // フィールドチェック
    meiMethods.handleSubmit(
      () => {
        const gyoNo: number = rowData?.gyoNo!;
        // 明細に設定するデータを取得
        const data: Data = handleDataMapping(gyoNo);

        // 明細を設定
        rows[gyoNo - 1] = data;
        setRows([...rows]);

        // 明細入力部クリア
        meiMethods.reset();
        resetMeiDirtyFields();
      },
      () => {
        focus(3);
      }
    )();
  };

  // 明細削除
  const handleClickDeleteMeiButton = () => {
    const gyoNo = rowData?.gyoNo!;
    // 対象の行を削除
    const newRows = rows.filter(function (source: Data) {
      return source.gyoNo !== gyoNo;
    });

    // 明細テーブルの行番号振り直し
    mapIds(newRows, "gyoNo");
    // 削除後のデータセット
    setRows(newRows);

    // 明細入力部クリア
    meiMethods.reset();
    resetMeiDirtyFields();
  };

  // excelボタンクリック時
  const handleMoveOutput = () => {
    // セット商品マスタ出力画面へ遷移する。
    setMenuNavigate("next");
    navigate("/b100133");
  };

  // セット商品コードの変更
  const HandleChangeSetshohinCd = async () => {
    const fields = methods.getValues();
    if (fields.setshohinCd === fields.dirtyFields?.setshohinCd) return;

    // セット商品コードが変更された場合、明細をクリア
    setRows([]);
    // 明細入力部クリア
    meiMethods.reset();
    resetMeiDirtyFields();
    setDelData([]);

    (async () => {
      await B000200SetshohinNm(methods, navigate, setMsg);
      resetDirtyFields();
    })();
  };

  // 商品コードの変更
  const HandleChangeShohinCd = async () => {
    const meiFields = meiMethods.getValues();
    if (meiFields.shohinCd === meiFields.dirtyFields?.shohinCd) return;

    (async () => {
      await B100130HandleChangeShohinCd(meiMethods, navigate, setMsg);
      resetMeiDirtyFields();
    })();
  };

  // WH単価変更
  const handleChangeWhTanka = () => {
    const meiFields = meiMethods.getValues();
    if (
      meiFields.sotosunW === meiFields.dirtyFields?.sotosunW &&
      meiFields.sotosunH === meiFields.dirtyFields?.sotosunH
    ) {
      return;
    }

    (async () => {
      await B100130WhTanka(meiMethods, navigate, setMsg);
      resetMeiDirtyFields();
    })();
  };

  //合計金額算出
  const handleGokeiCalculating = () => {
    // 明細が設定されていない場合
    if (rows.length === 0) {
      // 合計金額の初期化のみ行う
      methods.setValue("jodaitankaGokei", "");
      return;
    }
    // セット上代単価合計
    const jodaitankaGokei: string = rows
      .reduce((acc: Big, row: Data): Big => {
        return acc.plus(
          new Big(row.jodaitanka ? row.jodaitanka : "0").times(
            new Big(row.koseisu!)
          )
        );
      }, new Big("0"))
      .toString();

    methods.setValue("jodaitankaGokei", jodaitankaGokei);
  };

  // 明細のデータマッピング
  const handleDataMapping = (gyoNo: number): Data => {
    // フォームのデータを取得する。
    const meiFields = meiMethods.getValues();
    const data: Data = {
      gyoNo: gyoNo,
      shohinCd: meiFields.shohinCd!,
      shohinNm: meiFields.shohinNm,
      sotosunW: meiFields.sotosunW,
      sotosunH: meiFields.sotosunH,
      uchisunW: meiFields.uchisunW,
      uchisunH: meiFields.uchisunH,
      jodaitanka: meiFields.jodaitanka,
      koseisu: meiFields.koseisu,
      tankaKbn: meiFields.tankaKbn,
      whtankaCd: meiFields.whtankaCd,
      crtDate: meiFields.crtDate,
      crtshainCd: meiFields.crtshainCd,
      version: meiFields.version,
    };
    return data;
  };

  // レイアウト設定
  return (
    <>
      <FormProvider {...methods}>
        <SFrame
          title="セット商品マスタメンテ"
          gridTemplateColumns="10rem 10rem 15rem 5rem 3rem 7rem 7rem 7rem"
        >
          <SText gridColumn="span 7"></SText>
          <SFileButton
            label={labels.buttons.excel}
            align="right"
            {...register(13)}
            onClick={handleMoveOutput}
          />

          <SLabel required gridColumn="1">
            セット商品コード
          </SLabel>
          <SNumberField
            name="setshohinCd"
            label="セット商品コード"
            code
            required
            size={10}
            dialog={<B000200 />}
            handleSubmit={(data: any) => {
              methods.setValue("setshohinCd", data.shohinCd ?? "");
              methods.setValue("shohinNm", data.shohinNm ?? "");

              // 商品コードが変更された場合、明細をクリア
              if (
                data.shohinCd !== methods.getValues().dirtyFields?.setshohinCd
              ) {
                setRows([]);
                // 明細入力部クリア
                meiMethods.reset();
                resetMeiDirtyFields();
                setDelData([]);
              }
              resetDirtyFields();
            }}
            onBlur={HandleChangeSetshohinCd}
            {...register(1)}
          />
          <STextField name="shohinNm" disabled gridColumn="span 3" />
          <SText></SText>
          <SLabel gridColumn="6">セット上代単価</SLabel>

          <SText gridColumn="span 4"></SText>
          <SNumberField
            name="jodaitankaGokei"
            label="セット上代単価"
            gridColumn="6"
            align="right"
            disabled
          />
          <SText></SText>
          <SNormalButton
            label={labels.buttons.search}
            onClick={handleClickSearchButton}
            align="right"
            {...register(2)}
          />
        </SFrame>
      </FormProvider>

      <SFrame>
        <STable
          keyName="gyoNo"
          data={rows}
          columns={columns}
          tableRef={tableRef}
          handleDoubleClick={handleDoubleClickTableRow}
          width={1030}
          height={430}
          pageSize={15}
          onPageChange={handleResetSearch}
        />
      </SFrame>

      <FormProvider {...meiMethods}>
        <SFrame>
          <SEntryTab name="tab" handleOk={handleChangeTab}></SEntryTab>
          <SFrame gridTemplateColumns="8rem 15rem 3rem 1rem 3rem 3rem 1rem 3rem 6rem 6rem">
            <SLabel required>商品コード</SLabel>
            <SLabel>商品名</SLabel>
            <SLabel gridColumn="span 3">WH(外)</SLabel>
            <SLabel gridColumn="span 3">WH(内)</SLabel>
            <SLabel>上代</SLabel>
            <SLabel required>構成数</SLabel>

            <SNumberField
              name="shohinCd"
              required
              label="商品コード"
              code
              size={10}
              dialog={<B000200 />}
              handleSubmit={(data: any) => {
                meiMethods.setValue("shohinCd", data.shohinCd ?? "");
                meiMethods.setValue("shohinNm", data.shohinNm ?? "");
                meiMethods.setValue("tankaKbn", data.tankaKbn ?? "");
                meiMethods.setValue("whtankaCd", data.whtankaCd ?? "");
                // 商品関連項目設定処理
                (async () => {
                  await B100130Shohin(meiMethods, navigate, setMsg);
                  resetMeiDirtyFields();
                })();
              }}
              onBlur={HandleChangeShohinCd}
              {...register(3)}
            />
            <STextField name="shohinNm" disabled />
            <SNumberField
              name="sotosunW"
              size={4}
              onBlur={handleChangeWhTanka}
              {...register(4)}
            />
            <SText>×</SText>
            <SNumberField
              name="sotosunH"
              size={4}
              onBlur={handleChangeWhTanka}
              {...register(5)}
            />
            <SNumberField name="uchisunW" size={4} {...register(6)} />
            <SText>×</SText>
            <SNumberField name="uchisunH" size={4} {...register(7)} />
            <SNumberField
              name="jodaitanka"
              size={6}
              align="right"
              {...register(8)}
            />
            <SNumberField
              name="koseisu"
              required
              label="構成数"
              size={6}
              align="right"
              {...register(9)}
            />

            {!meiMethods.watch("tab") && (
              <SCancelButton
                label={labels.buttons.linedeletion}
                gridRow="3"
                align="left"
                {...register(12)}
                onClick={handleClickDeleteMeiButton}
              />
            )}

            {meiMethods.watch("tab") ? (
              <SNormalButton
                label={labels.buttons.createDetail}
                gridRow="3"
                gridColumn="10"
                align="right"
                {...register(10)}
                onClick={handleClickCreateMeiButton}
              />
            ) : (
              <SNormalButton
                label={labels.buttons.updateDetail}
                gridRow="3"
                gridColumn="10"
                align="right"
                {...register(11)}
                onClick={handleClickUpdateMeiButton}
              />
            )}
          </SFrame>
        </SFrame>
        <SFrame>
          <SNormalButton
            label={labels.buttons.update}
            align="right"
            {...register(12)}
            dialog={
              <SConfirmDialog
                title={labels.dialog.createMasterTitle}
                msg={labels.dialog.updateConfirm}
              />
            }
            dialogAction="confirm"
            handleSubmit={handleClickUpdateButton}
          />
        </SFrame>
      </FormProvider>
    </>
  );
};
